import { MDBBreadcrumb, MDBBreadcrumbItem } from 'mdb-react-ui-kit';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBreadcrumbContext } from './BreadcrumbContext';
import { breadcrumbRoutes } from './breadcrumbConfig';

/**
 * Matches a URL pathname against a route pattern with :param placeholders.
 * Returns { params } on match, or null on mismatch.
 */
function matchPattern(pathname, pattern) {
  const pathParts = pathname.replace(/\/$/, '').split('/');
  const patternParts = pattern.replace(/\/$/, '').split('/');

  if (pathParts.length !== patternParts.length) return null;

  const params = {};
  for (let i = 0; i < patternParts.length; i++) {
    if (patternParts[i].startsWith(':')) {
      params[patternParts[i]] = pathParts[i];
    } else if (patternParts[i] !== pathParts[i]) {
      return null;
    }
  }
  return { params };
}

/**
 * Finds the best matching route config for the given pathname.
 * Prefers the most specific match (most segments).
 */
function findMatchingRoute(pathname) {
  let bestMatch = null;
  let bestLength = -1;

  for (const route of breadcrumbRoutes) {
    const result = matchPattern(pathname, route.pattern);
    const segmentCount = route.pattern.split('/').length;
    if (result && segmentCount > bestLength) {
      bestMatch = { ...route, params: result.params };
      bestLength = segmentCount;
    }
  }
  return bestMatch;
}

/**
 * Replaces :param placeholders in a path string with actual values.
 */
function resolvePathParams(path, params) {
  if (!path) return path;
  return path.replace(/:([a-zA-Z]+)/g, (_, key) => params[':' + key] || _);
}

export default function GlobalBreadcrumb() {
  const { t } = useTranslation();
  const location = useLocation();
  const { dynamicLabels } = useBreadcrumbContext();

  // Don't render on home page
  if (location.pathname === '/' || location.pathname === '') return null;

  const match = findMatchingRoute(location.pathname);
  if (!match) return null;

  const { crumbs, params } = match;

  return (
    <MDBBreadcrumb className="mb-3 mt-2 pe-3 text-sm">
      {crumbs.map((crumb, i) => {
        const isLast = i === crumbs.length - 1;
        const resolvedPath = crumb.path ? resolvePathParams(crumb.path, params) : null;

        // Use dynamic label if available, otherwise translate the static label
        let label = t(crumb.label);
        if (crumb.param && dynamicLabels[crumb.param]) {
          label = dynamicLabels[crumb.param];
        }

        return (
          <MDBBreadcrumbItem key={i} active={isLast} current={isLast ? 'page' : undefined}>
            {isLast || !resolvedPath ? label : <Link to={resolvedPath}>{label}</Link>}
          </MDBBreadcrumbItem>
        );
      })}
    </MDBBreadcrumb >
  );
}
